import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sendApiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import { RichTextEditor } from '@mantine/rte';
import { MantineProvider } from '@mantine/core';
import { motion } from 'framer-motion';

const EditTopic = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    exam: '',
    wordsPerMin: '',
    wordLength: '',
    backspaceBlock: 'No',
    markingRule: '',
    mp3File: null,
    transcriptionTime: '',
    isPaid: 'Paid',
    description: '',
    customError: '',
    existingMp3Url: ''
  });
  const [exams, setExams] = useState([]);
  const [markingRules, setMarkingRules] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Calculate word length whenever description changes
    if (formData.description) {
      const wordCount = formData.description.trim().split(/\s+/).filter(word => word.length > 0).length;
      setFormData(prev => ({
        ...prev,
        wordLength: wordCount.toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        wordLength: '0'
      }));
    }
  }, [formData.description]);

  useEffect(() => {
    fetchTopic();
    fetchExams();
    fetchMarkingRules();
  }, [id]);

  const fetchTopic = async () => {
    try {
      const response = await sendApiRequest(`api/admin/topics/${id}`, {}, 'GET');
      if (response) {
        setFormData({
          title: response.title || '',
          exam: response.exam?._id || '',
          wordsPerMin: response.wordsPerMin || '',
          wordLength: response.wordLength || '',
          backspaceBlock: response.backspaceBlock ? 'Yes' : 'No',
          markingRule: response.markingRule?._id || '',
          mp3File: null,
          transcriptionTime: response.transcriptionTime || '',
          isPaid: response.isPaid ? 'Paid' : 'Demo',
          description: response.description || '',
          customError: response.customError || '',
          existingMp3Url: response.mp3File?.url || ''
        });
      }
      setLoading(false);
    } catch (error) {
      toast.error('Error fetching topic');
      setLoading(false);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await sendApiRequest('api/admin/exams', {}, 'GET');
      if (response && response.data) {
        setExams(response.data);
      } else {
        setExams([]);
      }
    } catch (err) {
      setError('Error fetching exams');
      toast.error('Error fetching exams');
    }
  };

  const fetchMarkingRules = async () => {
    try {
      const response = await sendApiRequest('api/marking-rules', {}, 'GET');
      if (response) {
        setMarkingRules(response);
      } else {
        setMarkingRules([]);
      }
    } catch (err) {
      setError('Error fetching marking rules');
      toast.error('Error fetching marking rules');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'mp3File') {
      setFormData(prev => ({ ...prev, mp3File: e.target.files[0] }));
    } else if (name === 'isPaid') {
      setFormData(prev => ({ ...prev, isPaid: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleRTEChange = (value) => {
    setFormData(prev => ({ ...prev, customError: value }));
  };

  const uploadToCloudinary = async (file) => {
    const formDataCloud = new FormData();
    formDataCloud.append("file", file);
    formDataCloud.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/video/upload`,
      {
        method: "POST",
        body: formDataCloud,
      }
    );
    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let mp3FileData = null;
      if (formData.mp3File) {
        mp3FileData = await uploadToCloudinary(formData.mp3File);
      }

      const topicData = {
        title: formData.title,
        exam: formData.exam,
        wordLength: formData.wordLength,
        wordsPerMin: formData.wordsPerMin,
        backspaceBlock: formData.backspaceBlock === 'Yes',
        markingRule: formData.markingRuleId,
        transcriptionTime: formData.transcriptionTime,
        isPaid: formData.isPaid === 'Paid',
        description: formData.description,
        customError: formData.customError
      };

      if (mp3FileData) {
        topicData.mp3File = {
          url: mp3FileData.secure_url,
          public_id: mp3FileData.public_id
        };
      }

      await sendApiRequest(`api/admin/topics/${id}`, topicData, 'PUT');
      setSuccess('Topic updated successfully!');
      toast.success('Topic updated successfully!');
      navigate('/admin-panel/topic-list');
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating topic');
      toast.error('Error updating topic');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg max-w-5xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg max-w-5xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-indigo-800 tracking-tight">
          Edit Topic
        </h2>
        <p className="text-indigo-600 mt-2">Update the details for this practice topic</p>
        <div className="h-1 w-20 bg-indigo-300 mt-3 rounded-full"></div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Topic Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-700 placeholder-indigo-300"
              placeholder="Enter topic title"
              required
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Exam</label>
            <select
              name="exam"
              value={formData.exam}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-700"
              required
            >
              <option value="">Select an exam</option>
              {exams.map(exam => (
                <option key={exam._id} value={exam._id}>{exam.name}</option>
              ))}
            </select>
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Word Length</label>
            <input
              type="text"
              name="wordLength"
              value={formData.wordLength}
              readOnly
              disabled
              className="w-full px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl text-indigo-700 cursor-not-allowed"
              placeholder="Calculated automatically"
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Backspace Block</label>
            <select
              name="backspaceBlock"
              value={formData.backspaceBlock}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-700"
              required
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Marking Rule</label>
            <select
              name="markingRule"
              value={formData.markingRule}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-700"
              required
            >
              <option value="">Select a marking rule</option>
              {markingRules.map(rule => (
                <option 
                key={rule._id} 
                value={rule._id}
                selected={formData.markingRule === rule._id}
              >
                {markingRules.find(r => r._id === rule._id)?.title}
              </option>
              ))}
            </select>
          </div>
          
          <div className="md:col-span-2 space-y-1">
            <label className="block text-sm font-medium text-indigo-700">
              Words Per Minute
              <span className="ml-2 text-xs text-indigo-500">(Numbers only, separated by commas)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="wordsPerMin"
                value={formData.wordsPerMin}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-700 placeholder-indigo-300"
                placeholder="Example: 60,80,120"
                required
                pattern="^\d+(,\d+)*$"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-indigo-400 text-sm">wpm</span>
              </div>
            </div>
          </div>
          
          <div className="md:col-span-2 space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="20"
              className="w-full px-4 py-3 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-700 placeholder-indigo-300"
              placeholder="Enter topic description"
              required
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Transcription Time</label>
            <div className="relative">
              <input
                type="number"
                name="transcriptionTime"
                value={formData.transcriptionTime}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-700"
                required
                min="0"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-indigo-400 text-sm">min</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Audio File</label>
            {formData.existingMp3Url && (
              <div className="mb-3 p-3 bg-indigo-50 rounded-lg">
                <p className="text-sm font-medium text-indigo-700 mb-2">Current Audio:</p>
                <div className="flex items-center space-x-4">
                  <audio controls className="flex-1">
                    <source src={formData.existingMp3Url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              </div>
            )}
            <div className="flex items-center space-x-4">
              <label className="flex-1">
                <div className="px-4 py-3 bg-white border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-all duration-300 cursor-pointer">
                  <span className="text-indigo-700">
                    {formData.mp3File ? formData.mp3File.name : 'Choose new file...'}
                  </span>
                  <input
                    type="file"
                    name="mp3File"
                    accept="audio/*"
                    onChange={handleChange}
                    className="hidden"
                  />
                </div>
              </label>
              {formData.mp3File && (
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, mp3File: null }))}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-indigo-500">MP3 format preferred</p>
          </div>
          
          <div className="md:col-span-2 space-y-1">
            <label className="block text-sm font-medium text-indigo-700">Topic Type</label>
            <div className="flex space-x-6">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="isPaid"
                  value="Paid"
                  checked={formData.isPaid === 'Paid'}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-indigo-300"
                />
                <span className="ml-2 text-gray-700">Paid</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="isPaid"
                  value="Demo"
                  checked={formData.isPaid === 'Demo'}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-indigo-300"
                />
                <span className="ml-2 text-gray-700">Demo</span>
              </label>
            </div>
          </div>
          
          <div className="md:col-span-2 space-y-1">
            <label className="block text-sm font-medium text-indigo-700">
              Custom Error Message
              <span className="ml-2 text-xs text-indigo-500">(Optional)</span>
            </label>
            <div className="border border-indigo-200 rounded-xl overflow-hidden">
              <RichTextEditor
                value={formData.customError}
                onChange={handleRTEChange}
                placeholder="Enter custom error message (optional)"
                style={{ background: 'white', minHeight: '200px' }}
                className="min-h-[200px]"
                styles={{
                  root: {
                    minHeight: '200px',
                    height: '200px',
                    border: 'none'
                  },
                  toolbar: {
                    borderBottom: '1px solid #e2e8f0',
                    backgroundColor: '#f8fafc'
                  }
                }}
              />
            </div>
          </div>
        </div>
        
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700"
          >
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </motion.div>
        )}
        
        {success && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700"
          >
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {success}
            </div>
          </motion.div>
        )}
        
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin-panel/topic-list')}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-3 rounded-xl font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 ${loading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'}`}
          >
            <div className="flex items-center justify-center">
              {loading && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Updating...' : 'Update Topic'}
            </div>
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditTopic;